import { useState, useCallback } from 'react';
import { useAuthContext } from '../../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export const useUserTracking = () => {
  const { user, deviceId, token } = useAuthContext();
  const [sessionId, setSessionId] = useState(null);
  const [quizlets, setQuizlets] = useState({}); // Store quizlet IDs by index

  // API call helper
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const url = `${API_BASE}/api/user-tracking${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, config);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'API call failed');
    }

    return data;
  }, [token]);

  // Create quiz session
  const createQuizSession = useCallback(async (quizConfig, quizData) => {
    try {
      let endpoint = '/quiz-session';
      let body = {
        quiz_config: quizConfig,
        quiz_data: quizData,
        total_duration_minutes: quizConfig.duration_minutes
      };

      // Handle different user types
      if (user?.user_type === 'registered') {
        body.user_id = user.id;
        body.device_id = deviceId;
      } else if (user?.user_type === 'guest') {
        body.user_id = user.id;
        body.device_id = deviceId;
      } else {
        // Anonymous player
        endpoint = '/anonymous-quiz-session';
        body.device_id = deviceId;
      }

      const response = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (response.success) {
        setSessionId(response.session_id);
        return response.session_id;
      }
      throw new Error('Failed to create quiz session');
    } catch (error) {
      console.error('Error creating quiz session:', error);
      throw error;
    }
  }, [user, deviceId, apiCall]);

  // Track quiz event
  const trackQuizEvent = useCallback(async (eventName, additionalData = {}, providedSessionId = null) => {
    const currentSessionId = providedSessionId || sessionId;
    
    if (!currentSessionId) {
      console.warn('No session ID available for tracking event:', eventName);
      return;
    }

    try {
      await apiCall('/track-event', {
        method: 'POST',
        body: JSON.stringify({
          session_id: currentSessionId,
          event_name: eventName,
          additional_data: additionalData
        }),
      });
    } catch (error) {
      console.error('Error tracking quiz event:', eventName, error);
    }
  }, [sessionId, apiCall]);

  // Create quizlet
  const createQuizlet = useCallback(async (songId, quizletIndex, quizletData, questionsCount) => {
    if (!sessionId) {
      console.warn('No session ID available for creating quizlet');
      return null;
    }

    try {
      const response = await apiCall('/quizlet', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          song_id: songId,
          quizlet_index: quizletIndex,
          quizlet_data: quizletData,
          questions_count: questionsCount
        }),
      });

      if (response.success) {
        setQuizlets(prev => ({
          ...prev,
          [quizletIndex]: response.quizlet_id
        }));
        return response.quizlet_id;
      }
      throw new Error('Failed to create quizlet');
    } catch (error) {
      console.error('Error creating quizlet:', error);
      return null;
    }
  }, [sessionId, apiCall]);

  // Create quizlet result
  const createQuizletResult = useCallback(async (quizletIndex, resultData) => {
    const quizletId = quizlets[quizletIndex];
    if (!quizletId) {
      console.warn('No quizlet ID available for quizlet index:', quizletIndex);
      return null;
    }

    try {
      const response = await apiCall('/quizlet-result', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          quizlet_id: quizletId,
          ...resultData
        }),
      });

      if (response.success) {
        return response.result_id;
      }
      throw new Error('Failed to create quizlet result');
    } catch (error) {
      console.error('Error creating quizlet result:', error);
      return null;
    }
  }, [sessionId, quizlets, apiCall]);

  // Track quizlet event
  const trackQuizletEvent = useCallback(async (quizletIndex, eventName, additionalData = {}) => {
    const quizletId = quizlets[quizletIndex];
    if (!quizletId) {
      console.warn('No quizlet ID available for tracking event');
      return;
    }

    try {
      await apiCall('/track-quizlet-event', {
        method: 'POST',
        body: JSON.stringify({
          result_id: quizletId,
          event_name: eventName,
          additional_data: additionalData
        }),
      });
    } catch (error) {
      console.error('Error tracking quizlet event:', error);
    }
  }, [quizlets, apiCall]);

  // Update quiz session
  const updateQuizSession = useCallback(async (updates) => {
    if (!sessionId) {
      console.warn('No session ID available for updating');
      return;
    }

    try {
      await apiCall(`/quiz-session/${sessionId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
    } catch (error) {
      console.error('Error updating quiz session:', error);
    }
  }, [sessionId, apiCall]);

  // Get user performance summary
  const getUserPerformanceSummary = useCallback(async () => {
    try {
      const response = await apiCall('/user-performance');
      return response.summary;
    } catch (error) {
      console.error('Error getting user performance summary:', error);
      return null;
    }
  }, [apiCall]);

  // Get quiz analytics
  const getQuizAnalytics = useCallback(async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const response = await apiCall(`/analytics?${queryParams}`);
      return response.analytics;
    } catch (error) {
      console.error('Error getting quiz analytics:', error);
      return [];
    }
  }, [apiCall]);

  // Reset tracking state
  const resetTracking = useCallback(() => {
    setSessionId(null);
    setQuizlets({});
  }, []);

  return {
    sessionId,
    quizlets,
    createQuizSession,
    trackQuizEvent,
    createQuizlet,
    createQuizletResult,
    trackQuizletEvent,
    updateQuizSession,
    getUserPerformanceSummary,
    getQuizAnalytics,
    resetTracking,
  };
};
